import type { ClassId } from './types';

/**
 * Gear is made of *instances*, not stack counts: every drop is its own object
 * with rolled bonuses and a name, so two swords of the same base can differ.
 *
 * Rolling happens on the server. The client only ever receives finished items
 * and renders them, which keeps the generation rules in one place.
 */
export type EquipSlot = 'weapon' | 'head' | 'body' | 'feet' | 'trinket';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic';

export interface StatBonus {
  attack?: number;
  defense?: number;
  maxHp?: number;
  maxMp?: number;
  speed?: number;
  /** Additive crit chance (0.02 = +2%). */
  crit?: number;
  str?: number;
  agi?: number;
  vit?: number;
}

export interface GearItem {
  uid: string;
  base: string;
  slot: EquipSlot;
  rarity: Rarity;
  ilvl: number;
  name: string;
  bonuses: StatBonus;
  /** Which set this piece belongs to, if any. */
  set?: string;
  /** Ids of the random affixes it rolled, so upgrades can add without repeating. */
  affixes?: string[];
  /** A named drop from a specific boss, rather than a random world find. */
  unique?: boolean;
}

export interface GearBase {
  id: string;
  name: string;
  slot: EquipSlot;
  /** Class-locked when set. Weapons are; armour never is. */
  classes?: ClassId[];
  /** Innate bonus at item level 1, scaled up with level. */
  bonus: StatBonus;
}

export const EQUIP_SLOTS: EquipSlot[] = ['weapon', 'head', 'body', 'feet', 'trinket'];

export const GEAR_BASES: GearBase[] = [
  { id: 'sword', name: 'Sword', slot: 'weapon', classes: ['warrior'], bonus: { attack: 4, str: 1 } },
  { id: 'bow', name: 'Bow', slot: 'weapon', classes: ['ranger'], bonus: { attack: 3, agi: 2 } },
  { id: 'staff', name: 'Staff', slot: 'weapon', classes: ['mage'], bonus: { attack: 4, maxMp: 8 } },
  { id: 'helm', name: 'Helm', slot: 'head', bonus: { defense: 2, maxHp: 12 } },
  { id: 'vest', name: 'Vest', slot: 'body', bonus: { defense: 3, maxHp: 18 } },
  { id: 'boots', name: 'Boots', slot: 'feet', bonus: { defense: 1, speed: 4 } },
  { id: 'charm', name: 'Charm', slot: 'trinket', bonus: { maxMp: 10, crit: 0.01 } },
];

const BASE_BY_ID = new Map(GEAR_BASES.map((base) => [base.id, base]));

export function gearBase(id: string): GearBase | undefined {
  return BASE_BY_ID.get(id);
}

export interface RarityDef {
  name: string;
  color: string;
  /** How many random affixes the rarity rolls. */
  affixes: number;
  /** Relative drop weight. */
  weight: number;
  /** Multiplier on the sale value. */
  value: number;
  /** Prefix added to the item's name. */
  prefix: string;
}

export const RARITIES: Record<Rarity, RarityDef> = {
  common: { name: 'Common', color: '#9aa4b2', affixes: 0, weight: 62, value: 1, prefix: '' },
  uncommon: { name: 'Uncommon', color: '#5cc46a', affixes: 1, weight: 24, value: 1.7, prefix: 'Fine ' },
  rare: { name: 'Rare', color: '#4f9de0', affixes: 2, weight: 11, value: 2.8, prefix: 'Superior ' },
  epic: { name: 'Epic', color: '#b06ce8', affixes: 3, weight: 3, value: 4.5, prefix: 'Masterwork ' },
};

export const RARITY_ORDER: Rarity[] = ['common', 'uncommon', 'rare', 'epic'];

export function isRarity(value: unknown): value is Rarity {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(RARITIES, value);
}

export function isEquipSlot(value: unknown): value is EquipSlot {
  return typeof value === 'string' && (EQUIP_SLOTS as string[]).includes(value);
}

/* --------------------------------------------------------------------- affixes */

interface Affix {
  id: string;
  /** Suffix used in the item name. */
  name: string;
  weight: number;
  roll: (tier: number) => StatBonus;
}

/**
 * A small pool on purpose: the variety comes from *combinations*, and a pool
 * this size is easy to reason about when balancing. The roll magnitude grows
 * with item level via `tier`.
 */
const AFFIXES: Affix[] = [
  { id: 'bear', name: 'of the Bear', weight: 10, roll: (t) => ({ vit: Math.max(1, Math.round(t)) }) },
  { id: 'fox', name: 'of the Fox', weight: 10, roll: (t) => ({ agi: Math.max(1, Math.round(t)) }) },
  { id: 'titan', name: 'of the Titan', weight: 10, roll: (t) => ({ str: Math.max(1, Math.round(t)) }) },
  { id: 'fortitude', name: 'of Fortitude', weight: 10, roll: (t) => ({ maxHp: Math.max(6, Math.round(11 * t)) }) },
  { id: 'insight', name: 'of Insight', weight: 9, roll: (t) => ({ maxMp: Math.max(4, Math.round(8 * t)) }) },
  { id: 'warding', name: 'of Warding', weight: 9, roll: (t) => ({ defense: Math.max(1, Math.round(1.5 * t)) }) },
  { id: 'fury', name: 'of Fury', weight: 9, roll: (t) => ({ attack: Math.max(1, Math.round(2 * t)) }) },
  { id: 'wind', name: 'of the Wind', weight: 7, roll: (t) => ({ speed: Math.max(2, Math.round(3 * t)) }) },
  {
    id: 'precision',
    name: 'of Precision',
    weight: 6,
    roll: (t) => ({ crit: Math.min(0.12, 0.01 + Math.round(t) * 0.006) }),
  },
];

const AFFIX_IDS = new Set(AFFIXES.map((affix) => affix.id));

export function isAffixId(value: unknown): value is string {
  return typeof value === 'string' && AFFIX_IDS.has(value);
}

/* ---------------------------------------------------------------------- sets */

export interface GearSet {
  id: string;
  /** Set name shown in tooltips. */
  name: string;
  /** Adjective woven into item names, e.g. "Boarhide Vest". */
  adjective: string;
  color: string;
  /** Base ids this set can roll on. */
  pieces: string[];
  /** Granted while two or more pieces are worn. */
  bonus2: StatBonus;
  /** Granted while three or more pieces are worn. */
  bonus3: StatBonus;
}

/**
 * Sets are deliberately small and overlapping so a character can mix two while
 * chasing a third. Bonuses scale off the *weakest* worn piece, so a set is only
 * as strong as the item you least want to replace.
 */
export const GEAR_SETS: GearSet[] = [
  {
    id: 'boarhide',
    name: 'Boarhide',
    adjective: 'Boarhide',
    color: '#a8703f',
    pieces: ['helm', 'vest', 'boots'],
    bonus2: { maxHp: 24, defense: 2 },
    bonus3: { maxHp: 42, defense: 4 },
  },
  {
    id: 'wispweave',
    name: 'Wispweave',
    adjective: 'Wispweave',
    color: '#4bb7f0',
    pieces: ['helm', 'vest', 'charm'],
    bonus2: { maxMp: 18, crit: 0.01 },
    bonus3: { maxMp: 32, crit: 0.02, attack: 3 },
  },
  {
    id: 'berserker',
    name: 'Berserker',
    adjective: 'Berserker',
    color: '#c0392b',
    pieces: ['sword', 'bow', 'staff', 'vest', 'boots'],
    bonus2: { attack: 4, str: 2 },
    bonus3: { attack: 8, str: 3, speed: 5 },
  },
  {
    id: 'wayfarer',
    name: 'Wayfarer',
    adjective: 'Wayfarer',
    color: '#5cc46a',
    pieces: ['boots', 'charm', 'helm'],
    bonus2: { speed: 10, maxHp: 16 },
    bonus3: { speed: 18, maxHp: 30, maxMp: 10 },
  },
];

const SET_BY_ID = new Map(GEAR_SETS.map((set) => [set.id, set]));

export function gearSet(item: GearItem): GearSet | undefined {
  return item.set ? SET_BY_ID.get(item.set) : undefined;
}

export function isGearSetId(value: unknown): value is string {
  return typeof value === 'string' && SET_BY_ID.has(value);
}

/** True when a set id exists and can legitimately appear on the given base. */
export function isValidSetFor(setId: unknown, baseId: string): boolean {
  if (!isGearSetId(setId)) return false;
  return SET_BY_ID.get(setId)?.pieces.includes(baseId) ?? false;
}

/* --------------------------------------------------------- signature drops */

export interface SignatureDrop {
  /** The landmark kind whose boss drops this. */
  poi: string;
  /** Base item it is built from. */
  base: string;
  name: string;
  set?: string;
  /** Extra bonus on top of the epic roll, scaled to the boss's level. */
  bonus: StatBonus;
}

/**
 * One named item per boss lair. A world boss always leaves its signature piece
 * alongside its normal loot, so each lair is worth hunting for something
 * specific rather than just "more gear". Affixes still roll, so a good copy is
 * still a chase.
 */
export const SIGNATURE_DROPS: Record<string, SignatureDrop> = {
  graveyard: {
    poi: 'graveyard',
    base: 'vest',
    name: "Grave-Wight's Shroud",
    set: 'wispweave',
    bonus: { maxHp: 60, crit: 0.02 },
  },
  ruins: {
    poi: 'ruins',
    base: 'helm',
    name: 'Colossus Crown',
    set: 'boarhide',
    bonus: { maxHp: 90, defense: 4 },
  },
  watchtower: {
    poi: 'watchtower',
    base: 'boots',
    name: "Warden's Treads",
    set: 'berserker',
    bonus: { attack: 6, speed: 6 },
  },
  camp: {
    poi: 'camp',
    base: 'boots',
    name: "Chieftain's Striders",
    set: 'wayfarer',
    bonus: { speed: 12, agi: 4 },
  },
  chapel: {
    poi: 'chapel',
    base: 'helm',
    name: "Fallen Priest's Mitre",
    set: 'wispweave',
    bonus: { maxMp: 40, crit: 0.02 },
  },
};

export function signatureFor(poi: string): SignatureDrop | undefined {
  return SIGNATURE_DROPS[poi];
}

/** Builds a boss's signature item at the given level, affixes and all. */
export function rollSignature(
  poi: string,
  level: number,
  rand: () => number = Math.random,
): GearItem | null {
  const def = SIGNATURE_DROPS[poi];
  if (!def) return null;

  const item = rollGear(def.base, level, 'epic', rand);
  item.name = def.name;
  item.unique = true;
  if (def.set) item.set = def.set;

  const extra = scaleBonus(def.bonus, Math.max(1, Math.round(level)));
  for (const key of Object.keys(extra) as Array<keyof StatBonus>) {
    const value = extra[key];
    if (value === undefined) continue;
    item.bonuses[key] = (item.bonuses[key] ?? 0) + value;
  }
  return item;
}

/** How many worn pieces belong to a given set. */
export function setCount(gear: Partial<Record<EquipSlot, GearItem>>, setId: string): number {
  let count = 0;
  for (const slot of EQUIP_SLOTS) {
    if (gear[slot]?.set === setId) count++;
  }
  return count;
}

/** The total bonus granted by every set currently worn. */
export function setBonus(gear: Partial<Record<EquipSlot, GearItem>>): StatBonus {
  const total: StatBonus = {};
  for (const set of GEAR_SETS) {
    const count = setCount(gear, set.id);
    if (count < 2) continue;
    // Scale off the weakest worn piece of the set.
    let lowest = Infinity;
    for (const slot of EQUIP_SLOTS) {
      const item = gear[slot];
      if (item?.set === set.id) lowest = Math.min(lowest, item.ilvl);
    }
    if (!Number.isFinite(lowest)) continue;
    addInto(total, scaleBonus(set.bonus2, lowest));
    if (count >= 3) addInto(total, scaleBonus(set.bonus3, lowest));
  }
  return total;
}

export interface ActiveSet {
  set: GearSet;
  count: number;
  /** The set's bonus at the current count, already scaled. */
  bonus: StatBonus;
}

/** Sets the player is currently benefiting from, for the equipment panel. */
export function activeSets(gear: Partial<Record<EquipSlot, GearItem>>): ActiveSet[] {
  const out: ActiveSet[] = [];
  for (const set of GEAR_SETS) {
    const count = setCount(gear, set.id);
    if (count < 2) continue;
    let lowest = Infinity;
    for (const slot of EQUIP_SLOTS) {
      const item = gear[slot];
      if (item?.set === set.id) lowest = Math.min(lowest, item.ilvl);
    }
    const bonus: StatBonus = {};
    addInto(bonus, scaleBonus(set.bonus2, lowest));
    if (count >= 3) addInto(bonus, scaleBonus(set.bonus3, lowest));
    out.push({ set, count, bonus });
  }
  return out;
}

/* ------------------------------------------------------------------- rolling */

let uidCounter = 0;

function makeUid(): string {
  uidCounter = (uidCounter + 1) % 1_000_000;
  return `${Date.now().toString(36)}-${uidCounter.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function addInto(target: StatBonus, extra: StatBonus): void {
  for (const key of Object.keys(extra) as Array<keyof StatBonus>) {
    const value = extra[key];
    if (value === undefined) continue;
    target[key] = (target[key] ?? 0) + value;
  }
}

/** Base bonuses scale with item level; crit scales gently so it cannot run away. */
export function scaleBonus(bonus: StatBonus, ilvl: number): StatBonus {
  const t = 1 + (ilvl - 1) * 0.12;
  const out: StatBonus = {};
  for (const key of Object.keys(bonus) as Array<keyof StatBonus>) {
    const value = bonus[key];
    if (value === undefined) continue;
    out[key] = key === 'crit' ? value * (1 + (ilvl - 1) * 0.05) : Math.max(1, Math.round(value * t));
  }
  return out;
}

function affixTier(ilvl: number): number {
  return 1 + (ilvl - 1) * 0.14;
}

function pickRarity(rand: () => number): Rarity {
  const total = RARITY_ORDER.reduce((sum, r) => sum + RARITIES[r].weight, 0);
  let target = rand() * total;
  for (const rarity of RARITY_ORDER) {
    target -= RARITIES[rarity].weight;
    if (target <= 0) return rarity;
  }
  return 'common';
}

function pickAffix(rand: () => number, used: Set<string>): Affix | undefined {
  const pool = AFFIXES.filter((a) => !used.has(a.id));
  if (pool.length === 0) return undefined;
  const total = pool.reduce((sum, a) => sum + a.weight, 0);
  let target = rand() * total;
  for (const affix of pool) {
    target -= affix.weight;
    if (target <= 0) return affix;
  }
  return pool[pool.length - 1];
}

/** Chance a piece belongs to a set at all. Not everything is set gear. */
const SET_CHANCE = 0.5;

export function rollGear(
  baseId: string,
  ilvl: number,
  rarity: Rarity = pickRarity(Math.random),
  rand: () => number = Math.random,
): GearItem {
  const base = gearBase(baseId) ?? GEAR_BASES[0];
  const level = Math.max(1, Math.round(ilvl));
  const bonuses = scaleBonus(base.bonus, level);

  const used = new Set<string>();
  const affixNames: string[] = [];
  const affixIds: string[] = [];
  for (let i = 0; i < RARITIES[rarity].affixes; i++) {
    const affix = pickAffix(rand, used);
    if (!affix) break;
    used.add(affix.id);
    affixIds.push(affix.id);
    addInto(bonuses, affix.roll(affixTier(level)));
    affixNames.push(affix.name);
  }

  const candidates = GEAR_SETS.filter((set) => set.pieces.includes(base.id));
  const set = candidates.length > 0 && rand() < SET_CHANCE ? candidates[Math.floor(rand() * candidates.length)] : undefined;

  const name = `${RARITIES[rarity].prefix}${set ? `${set.adjective} ` : ''}${base.name}${
    affixNames.length ? ` ${affixNames.join(' ')}` : ''
  }`;

  return {
    uid: makeUid(),
    base: base.id,
    slot: base.slot,
    rarity,
    ilvl: level,
    name,
    bonuses,
    set: set?.id,
    affixes: affixIds,
  };
}

/** Any base that could drop; weapons are biased to the given class. */
export function rollRandomBase(ilvl: number, classId: ClassId, rand: () => number = Math.random): string {
  const weapons = GEAR_BASES.filter((b) => b.slot === 'weapon' && b.classes?.includes(classId));
  const armour = GEAR_BASES.filter((b) => b.slot !== 'weapon');
  const pool = [...weapons, ...armour];
  return pool[Math.floor(rand() * pool.length)]?.id ?? 'vest';
}

/** A weapon a member of this class can actually use. */
export function rollWeaponBase(classId: ClassId, rand: () => number = Math.random): string {
  const pool = GEAR_BASES.filter((b) => b.slot === 'weapon' && b.classes?.includes(classId));
  return pool[Math.floor(rand() * pool.length)]?.id ?? 'sword';
}

/** Any armour piece (head, body, feet or trinket). */
export function rollArmourBase(rand: () => number = Math.random): string {
  const pool = GEAR_BASES.filter((b) => b.slot !== 'weapon');
  return pool[Math.floor(rand() * pool.length)]?.id ?? 'vest';
}

/* ---------------------------------------------------------------- aggregation */

export function gearBonus(gear: Partial<Record<EquipSlot, GearItem>>): StatBonus {
  const total: StatBonus = {};
  for (const slot of EQUIP_SLOTS) {
    const item = gear[slot];
    if (item) addInto(total, item.bonuses);
  }
  return total;
}

/** Every bonus the worn gear grants: the pieces themselves plus any active set. */
export function totalGearBonus(gear: Partial<Record<EquipSlot, GearItem>>): StatBonus {
  const total = gearBonus(gear);
  addInto(total, setBonus(gear));
  return total;
}

export function canClassEquip(baseId: string, classId: ClassId): boolean {
  const base = gearBase(baseId);
  if (!base || !base.classes) return true;
  return base.classes.includes(classId);
}

export function gearValue(item: GearItem): number {
  const base = 5 + item.ilvl * 2.5;
  return Math.max(1, Math.round(base * RARITIES[item.rarity].value));
}

/** What the trader charges for a piece. Buying costs several times what selling pays. */
export function gearPrice(item: GearItem): number {
  return Math.max(1, Math.round(gearValue(item) * 4));
}

const RARITY_STEP: Record<Rarity, Rarity | null> = {
  common: 'uncommon',
  uncommon: 'rare',
  rare: 'epic',
  epic: null,
};

export function nextRarity(rarity: Rarity): Rarity | null {
  return RARITY_STEP[rarity];
}

/**
 * Coins to push an item up one rarity. It scales with item level so the grind
 * stays roughly the same number of kills at every level, and epic steps cost
 * three times a rare step.
 */
export function upgradeCost(item: GearItem): number | null {
  const next = nextRarity(item.rarity);
  if (!next) return null;
  const base = Math.round(150 * Math.max(1, item.ilvl));
  const mult = next === 'uncommon' ? 0.5 : next === 'rare' ? 1 : 3;
  return Math.round(base * mult);
}

/**
 * Moves an item up one rarity, adding the affix(es) that step grants and
 * relabelling it. Mutates the item in place and returns it.
 */
export function upgradeGear(item: GearItem, rand: () => number = Math.random): GearItem {
  const from = item.rarity;
  const next = nextRarity(from);
  if (!next) return item;

  const used = new Set(item.affixes ?? []);
  const added = RARITIES[next].affixes - RARITIES[from].affixes;
  const newNames: string[] = [];
  const ids = [...used];
  for (let i = 0; i < added; i++) {
    const affix = pickAffix(rand, used);
    if (!affix) break;
    used.add(affix.id);
    ids.push(affix.id);
    addInto(item.bonuses, affix.roll(affixTier(item.ilvl)));
    newNames.push(affix.name);
  }

  item.rarity = next;
  item.affixes = ids;

  const oldPrefix = RARITIES[from].prefix;
  let name = item.name;
  if (oldPrefix && name.startsWith(oldPrefix)) name = name.slice(oldPrefix.length);
  item.name = `${RARITIES[next].prefix}${name}${newNames.length ? ` ${newNames.join(' ')}` : ''}`;
  return item;
}

/** The bonus text shown in a tooltip, one line per stat. */
export function describeBonus(bonus: StatBonus): string[] {
  const lines: string[] = [];
  if (bonus.attack) lines.push(`+${bonus.attack} Attack`);
  if (bonus.defense) lines.push(`+${bonus.defense} Defence`);
  if (bonus.maxHp) lines.push(`+${bonus.maxHp} Max Health`);
  if (bonus.maxMp) lines.push(`+${bonus.maxMp} Max Mana`);
  if (bonus.speed) lines.push(`+${bonus.speed} Move Speed`);
  if (bonus.crit) lines.push(`+${(bonus.crit * 100).toFixed(1)}% Crit Chance`);
  if (bonus.str) lines.push(`+${bonus.str} STR`);
  if (bonus.agi) lines.push(`+${bonus.agi} AGI`);
  if (bonus.vit) lines.push(`+${bonus.vit} VIT`);
  return lines;
}
